import React, { useMemo, useState } from 'react';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';
import Alert from '@mui/material/Alert';
import Popover from '@mui/material/Popover';
import { IconButton, Tooltip } from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import LanguageIcon from '@mui/icons-material/Language';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PersonIcon from '@mui/icons-material/Person';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import GavelIcon from '@mui/icons-material/Gavel';
import moment from 'moment';

import { CompanyDetail } from '../../interfaces/companyDetail.interface';
import { billingFrequencyOptions } from '../../interfaces/billing.interface';
import { USER_TYPES } from '../../services/user';
import { COMPANY_TYPES } from '../../services/vendor';
import { ACCENT_BLUE, SUCCESS_GREEN } from '../Assessments/Workspace/qcTheme';

const LEGACY_ASSESSMENTS_URL = 'https://mpaa.app.box.com/folder/188662031553';

// Card/typography/divider language is shared with the v2 assessment workspace header
// (Assessments/Workspace/WorkspaceHeader.tsx) so both surfaces read as one system.
const HeaderCard = styled('div')({
  background: '#fff',
  borderRadius: '4px',
  boxShadow: '0 2px 1px -1px rgba(0,0,0,0.2),0 1px 1px 0 rgba(0,0,0,0.14),0 1px 3px 0 rgba(0,0,0,0.12)',
  padding: '16px 24px',
  position: 'relative',
  marginBottom: '24px',
});

const HeaderRow = styled('div')({
  display: 'flex',
  alignItems: 'stretch',
  gap: '20px',
  flexWrap: 'wrap',
  paddingRight: '28px',
});

const AltNames = styled('span')({
  fontSize: '0.875rem',
  lineHeight: 1.5,
  color: 'rgba(0,0,0,0.6)',
});

const ParentLine = styled('span')({
  display: 'inline-flex',
  alignItems: 'baseline',
  gap: '0.35ch',
  fontSize: '0.875rem',
  color: 'rgba(0,0,0,0.6)',
  '& .parent-name': {
    fontWeight: 600,
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});

const ParentLink = styled('a')({
  fontWeight: 600,
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  textDecoration: 'none',
  '&, &:link, &:visited, &:hover, &:focus, &:active': { color: 'rgba(0,0,0,0.6)' },
  '&:focus-visible': { outline: '2px solid', outlineOffset: 2 },
});

const AssessorMeta = styled('div')({
  display: 'flex',
  gap: '16px',
  marginTop: '4px',
  paddingTop: '4px',
  borderTop: '1px solid rgba(0,0,0,0.12)',
  fontSize: '0.875rem',
  color: 'rgba(0,0,0,0.6)',
});

const BlockDivider = styled('div')({
  width: '1px',
  alignSelf: 'stretch',
  background: 'rgba(0,0,0,0.12)',
  margin: '0 4px',
  '@media (max-width:1100px)': { display: 'none' },
});

// Blocks grow into the available width and wrap at their basis rather than holding a
// fixed column, so the card reflows with the window instead of leaving a dead gutter.
const Block = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  minWidth: 0,
  flex: '1 1 200px',
});

// Address and Billing Address carry multi-line postal data, so they get a wider basis.
// Identical flex on both keeps the two columns the same width at any size.
const AddressBlock = styled(Block)({ flex: '1 1 260px' });

const Caption = styled('span')({
  fontSize: '0.78rem',
  letterSpacing: '0.5px',
  fontWeight: 600,
  color: 'rgba(0,0,0,0.6)',
  textTransform: 'uppercase',
});

// Off-grid 6px is load-bearing: 18px icon + 6px gap = SubLine's 24px indent below.
const Line = styled('span')({
  display: 'inline-flex',
  alignItems: 'flex-start',
  gap: '6px',
  fontSize: '0.875rem',
  fontWeight: 400,
  color: '#424242',
  minWidth: 0,
  '& .MuiSvgIcon-root': {
    fontSize: '18px',
    color: 'rgba(0,0,0,0.42)',
    flexShrink: 0,
    marginTop: '1px',
  },
});

// Continuation of an icon line (address rows), aligned under the first line's text.
const SubLine = styled('span')({
  paddingLeft: '24px',
  fontSize: '0.875rem',
  fontWeight: 400,
  color: '#424242',
});

const LinkLine = styled('a')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  fontSize: '0.875rem',
  minWidth: 0,
  '&, &:link, &:visited, &:hover, &:focus, &:active': { color: ACCENT_BLUE },
  '& .MuiSvgIcon-root': { fontSize: '18px', color: 'rgba(0,0,0,0.42)', flexShrink: 0 },
  '& .link-text': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
});

// 600 means "the value half of a label: value pair" — the only place weight carries meaning.
// An absent value stays 400 and muted so the eye is not drawn to a placeholder.
const MetaLine = styled('span')({
  fontSize: '0.875rem',
  color: 'rgba(0,0,0,0.6)',
  '& .meta-value': { fontWeight: 600, color: '#424242' },
  '& .meta-empty': { color: 'rgba(0,0,0,0.38)' },
});

const BillingIdButton = styled('span')({
  fontWeight: 600,
  color: ACCENT_BLUE,
  cursor: 'pointer',
  textDecoration: 'underline',
});

const SkeletonBlock = styled('div')({ flex: 1, minWidth: '160px' });

// Renders the logo filling the tile; on a load failure it swaps to the
// fallback (initials) so a broken file never surfaces a broken-image icon. Keyed by
// src at the call site, so the failed state resets when the logo changes.
export function LogoImage({ src, alt, fallback }: { src: string; alt: string; fallback: React.ReactNode }) {
  const [failed, setFailed] = useState(false);
  if (failed) return <>{fallback}</>;
  return (
    <img
      src={src}
      alt={alt}
      onError={() => setFailed(true)}
      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
    />
  );
}

function getQualifications(detail: CompanyDetail): string {
  const qualifications: string[] = [];
  if (detail.canAssessPrem) qualifications.push('Site');
  if (detail.canAssessCloud) qualifications.push('Cloud');
  if (detail.assessorProfile?.hasZeroTrustCertification) qualifications.push('Zero Trust');
  return qualifications.join(', ');
}

// Billing address is admin-only and never shown for assessors. It renders even when it
// matches the company address ("Same as Above"), so the column is always present.
function showBillingAddress(detail: CompanyDetail, userCanAdminCompany: boolean): boolean {
  return userCanAdminCompany && detail.type !== COMPANY_TYPES.ASSESSOR;
}

function cityStateZip(city?: string, state?: string, zipcode?: string): string {
  const cityState = [city, state].filter(Boolean).join(', ');
  return [cityState, zipcode].filter(Boolean).join(' ');
}

// Admin rows keep their label when the value is unset (v1 parity) with a dash placeholder.
const EM_DASH = '—';

function MetaValue({ children }: { children?: React.ReactNode }): JSX.Element {
  if (children === undefined || children === null || children === '') {
    return <span className="meta-empty">{EM_DASH}</span>;
  }
  return <span className="meta-value">{children}</span>;
}

export interface CompanyProfileHeaderProps {
  userType?: string;
  userCanAdminCompany: boolean;
  actionsMenuOpen: boolean;
  onActionsMenuClick: (event: React.MouseEvent<HTMLElement>) => void;
  detail: CompanyDetail | null;
  loading: boolean;
  onAddLogoClick: () => void;
}

export default function CompanyProfileHeader(props: CompanyProfileHeaderProps) {
  const {
    userType,
    userCanAdminCompany,
    actionsMenuOpen,
    onActionsMenuClick,
    detail,
    loading,
    onAddLogoClick,
  } = props;

  const [billingAnchor, setBillingAnchor] = useState<HTMLElement | null>(null);

  const isTpnAdmin = userType === USER_TYPES.TPN_ADMIN;
  const canClickSubsidiariesLink = useMemo(
    () => isTpnAdmin || userType === USER_TYPES.CONTENT_OWNER,
    [isTpnAdmin, userType],
  );

  const qualifications = useMemo(() => (detail ? getQualifications(detail) : ''), [detail]);

  if (loading || !detail) {
    return (
      <HeaderCard>
        <HeaderRow>
          {[0, 1, 2, 3].map((column) => (
            <SkeletonBlock key={column}>
              <Skeleton variant="rectangular" height={20} sx={{ mb: 1, borderRadius: 1 }} />
              <Skeleton variant="text" />
              <Skeleton variant="text" width="70%" />
            </SkeletonBlock>
          ))}
        </HeaderRow>
      </HeaderCard>
    );
  }

  const contact = detail.contact;
  const billingContact = detail.billingContact;
  const primaryContact = detail.primaryContactUser;
  const legalContact = detail.companyLegalContacts?.[0];
  const showFinancials = userCanAdminCompany && detail.type !== COMPANY_TYPES.ASSESSOR;
  const billingFrequencyLabel = billingFrequencyOptions.find((o) => o.value === detail.billingFrequency)?.key;
  const billingActive = detail.billingStatus === 'active';
  const companyAddress = cityStateZip(contact?.city, contact?.state, contact?.zipcode);
  // "Same as Above" stores no separate billing contact, so mirror the company's own.
  const billingSource = billingContact ?? contact;
  const billingAddress = cityStateZip(billingSource?.city, billingSource?.state, billingSource?.zipcode);
  const showDetailsBlock = showFinancials || userCanAdminCompany;
  // The company identity (logo + name) lives in the profile sub-nav; this block only
  // renders the identity-adjacent meta that has nowhere else to go.
  const hasIdentityMeta = Boolean(
    (detail.parentCompany && isTpnAdmin) ||
    (!detail.logo && userCanAdminCompany) ||
    detail.type === COMPANY_TYPES.CONTENT_OWNER ||
    detail.type === COMPANY_TYPES.ASSESSOR,
  );
  const hasAltNames = Boolean(detail.akaNames?.length || detail.fkaNames?.length || detail.dbaNames?.length);
  const hasBillingAddress = Boolean(
    billingSource?.name || detail.billingStatementName || billingSource?.address ||
    billingSource?.address2 || billingSource?.address3 || billingAddress ||
    billingSource?.country || billingSource?.phoneNumber,
  );

  return (
    <HeaderCard>
      {userCanAdminCompany && (
        <Tooltip title="Company Actions">
          <IconButton
            onClick={onActionsMenuClick}
            size="small"
            sx={{ position: 'absolute', top: 8, right: 8 }}
            aria-controls={actionsMenuOpen ? 'company-actions-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={actionsMenuOpen ? 'true' : undefined}
          >
            <MoreHorizIcon />
          </IconButton>
        </Tooltip>
      )}
      <HeaderRow>
        {(primaryContact || hasIdentityMeta) && (
          <>
            <Block>
              {primaryContact && <Caption>Primary Contact</Caption>}
              {primaryContact && <Line><PersonIcon />{primaryContact.firstName} {primaryContact.lastName}</Line>}
              {primaryContact?.email && (
                <Tooltip title={primaryContact.email}>
                  <LinkLine href={`mailto:${primaryContact.email}`}>
                    <MailOutlineIcon />
                    <span className="link-text">{primaryContact.email}</span>
                  </LinkLine>
                </Tooltip>
              )}
              {primaryContact?.contact?.phoneNumber && <Line><LocalPhoneIcon />{primaryContact.contact.phoneNumber}</Line>}
              {hasIdentityMeta && (
                <Box sx={{ mt: 'auto', pt: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {(!!detail.parentCompany && isTpnAdmin) && (
                    <ParentLine>
                      <span>A</span>
                      {canClickSubsidiariesLink ? (
                        <ParentLink href={`/registry/${detail.parentCompany.id}`} title={detail.parentCompany.name}>
                          {detail.parentCompany.name}
                        </ParentLink>
                      ) : (
                        <span className="parent-name" title={detail.parentCompany.name}>{detail.parentCompany.name}</span>
                      )}
                      <span>company</span>
                    </ParentLine>
                  )}
                  {(!detail.logo && userCanAdminCompany) && (
                    <Box><Button size="small" variant="outlined" onClick={onAddLogoClick} sx={{ mt: 0.5 }}>add logo</Button></Box>
                  )}
                  {detail.type === COMPANY_TYPES.CONTENT_OWNER && (
                    <LinkLine href={LEGACY_ASSESSMENTS_URL} target="_blank" rel="noopener noreferrer">
                      <span className="link-text">View Legacy Assessments</span>
                    </LinkLine>
                  )}
                  {detail.type === COMPANY_TYPES.ASSESSOR && (
                    <AssessorMeta>
                      <span>Level: {detail.assessorLevel?.descriptor}</span>
                      {qualifications && <span>Qualifications: {qualifications}</span>}
                    </AssessorMeta>
                  )}
                </Box>
              )}
              {legalContact && (
                <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <Caption>Legal Contact</Caption>
                    {legalContact.name && <Line><GavelIcon />{legalContact.name}</Line>}
                    {legalContact.email && (
                      <LinkLine href={`mailto:${legalContact.email}`} title={legalContact.email}>
                        <MailOutlineIcon />
                        <span className="link-text">{legalContact.email}</span>
                      </LinkLine>
                    )}
                    {legalContact.address && <SubLine>{legalContact.address}</SubLine>}
                </Box>
              )}
            </Block>
          </>
        )}

        {contact && (
          <>
            <BlockDivider />
            <AddressBlock>
              <Caption>Address</Caption>
              {contact.address && <Line><LocationOnIcon />{contact.address}</Line>}
              {contact.address2 && <SubLine>{contact.address2}</SubLine>}
              {contact.address3 && <SubLine>{contact.address3}</SubLine>}
              {companyAddress && <SubLine>{companyAddress}</SubLine>}
              {contact.country && <SubLine>{contact.country}</SubLine>}
              {contact.phoneNumber && <Line><LocalPhoneIcon />{contact.phoneNumber}</Line>}
              {detail.domain && (
                <LinkLine
                  href={detail.domain.startsWith('http') ? detail.domain : `https://${detail.domain}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={detail.domain}
                >
                  <LanguageIcon />
                  <span className="link-text">{detail.domain}</span>
                </LinkLine>
              )}
            </AddressBlock>
          </>
        )}

        {hasBillingAddress && showBillingAddress(detail, userCanAdminCompany) && (
          <>
            <BlockDivider />
            <AddressBlock>
              <Caption>Billing Address</Caption>
              {billingSource?.name && <Line><ReceiptLongIcon />{billingSource.name}</Line>}
              {detail.billingStatementName && <SubLine>{detail.billingStatementName}</SubLine>}
              {billingSource?.address && <SubLine>{billingSource.address}</SubLine>}
              {billingSource?.address2 && <SubLine>{billingSource.address2}</SubLine>}
              {billingSource?.address3 && <SubLine>{billingSource.address3}</SubLine>}
              {billingAddress && <SubLine>{billingAddress}</SubLine>}
              {billingSource?.country && <SubLine>{billingSource.country}</SubLine>}
              {billingSource?.phoneNumber && <Line><LocalPhoneIcon />{billingSource.phoneNumber}</Line>}
            </AddressBlock>
          </>
        )}

        {(showDetailsBlock || hasAltNames) && (
          <>
            <BlockDivider />
            <Block>
              <Caption>Company Details</Caption>
              {showFinancials && (
                <MetaLine>Annual Gross Revenue: <MetaValue>{detail.grossRevenue}</MetaValue></MetaLine>
              )}
              {showFinancials && (
                <MetaLine>Employee Count: <MetaValue>{detail.employeeCount}</MetaValue></MetaLine>
              )}
              {userCanAdminCompany && (
                <MetaLine>
                  Billing Customer ID:{' '}
                  {isTpnAdmin && detail.billingCustomerId
                    ? <BillingIdButton onClick={(e) => setBillingAnchor(e.currentTarget)}>{detail.billingCustomerId}</BillingIdButton>
                    : <MetaValue>{detail.billingCustomerId}</MetaValue>}
                </MetaLine>
              )}
              {isTpnAdmin && (
                <MetaLine>Billing Frequency: <MetaValue>{billingFrequencyLabel}</MetaValue></MetaLine>
              )}
              {userCanAdminCompany && (
                <MetaLine>Billing PO Number: <MetaValue>{detail.billingPoNumber}</MetaValue></MetaLine>
              )}
              {userCanAdminCompany && (
                <MetaLine>VAT Number: <MetaValue>{detail.vatNumber}</MetaValue></MetaLine>
              )}
              {hasAltNames && (
                <AltNames>
                  {!!detail.akaNames?.length && <span>AKA: {detail.akaNames.join(', ')}</span>}
                  {!!detail.fkaNames?.length && <span><br />FKA: {detail.fkaNames.join(', ')}</span>}
                  {!!detail.dbaNames?.length && <span><br />DBA: {detail.dbaNames.join(', ')}</span>}
                </AltNames>
              )}
            </Block>
          </>
        )}

      </HeaderRow>

      <Popover
        id="billing-customer-id-popover"
        open={Boolean(billingAnchor)}
        anchorEl={billingAnchor}
        onClose={() => setBillingAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {billingActive
          ? <Alert severity="success" sx={{ backgroundColor: '#FFF', color: SUCCESS_GREEN }}>Active through: {moment(detail.billingPaidThroughDate).format('MM/DD/YYYY')}</Alert>
          : <Alert severity="error" sx={{ backgroundColor: '#FFF' }}>Inactive - {moment(detail.billingPaidThroughDate).isAfter(moment()) ? 'Expiring' : 'Expired'}: {moment(detail.billingPaidThroughDate).format('MM/DD/YYYY')}</Alert>}
      </Popover>
    </HeaderCard>
  );
}
